import { useEffect, useId, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { resolveEffectiveRole } from "../../utils/roleResolver";
import { createPart3OperationId, requestPart3LaunchLocation } from "../../lib/part3LaunchLocation";
import { takePart3Command } from "../../lib/part3CommandHandoff";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const PRESET_URL = `${API}/api/work-mode/part3-presets/peterborough`;
const clean = (value) => typeof value === "string" ? value.trim() : "";
const normalName = (value) => clean(value).replace(/\s+/g, " ").toLocaleLowerCase("en-GB");
const locationMessages = {
  denied: "Allow location access for NDG, then try Go to pupil again.",
  timeout: "Your location took too long. Check your signal and try Go to pupil again.",
  unavailable: "A fresh location is unavailable. Check location services and try Go to pupil again.",
};

export function part3PupilOptions(rows) {
  const seen = new Set();
  return (Array.isArray(rows) ? rows : []).filter((row) => row && typeof row === "object").map((row) => ({
    id: clean(row.id),
    name: clean(row.name) || clean(row.full_name)
      || [clean(row.first_name), clean(row.last_name)].filter(Boolean).join(" "),
  })).filter((row) => {
    if (!row.id || !row.name || seen.has(row.id)) return false;
    seen.add(row.id);
    return true;
  }).sort((a, b) => a.name.localeCompare(b.name, "en-GB"));
}

export function exactPart3Pupil(rows, query) {
  const name = normalName(query);
  if (!name) return null;
  const matches = rows.filter((row) => normalName(row.name) === name);
  return matches.length === 1 ? matches[0] : null;
}

function launchError(error) {
  const detail = error?.response?.data?.detail;
  return clean(detail) || clean(detail?.message)
    || "The Part 3 route could not start. Your pupil is still selected; try again.";
}

function PresetForm({ command }) {
  const navigate = useNavigate();
  const fieldId = useId();
  const [preset, setPreset] = useState(null);
  const [activeLaunch, setActiveLaunch] = useState(null);
  const [pupils, setPupils] = useState([]);
  const [query, setQuery] = useState("");
  const [studentId, setStudentId] = useState("");
  const [loadState, setLoadState] = useState("loading");
  const [reload, setReload] = useState(0);
  const [status, setStatus] = useState("ready");
  const [error, setError] = useState("");
  const operationRef = useRef(null);
  const attemptRef = useRef(null);
  const aliveRef = useRef(true);
  const commandHandledRef = useRef(null);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      attemptRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setLoadState("loading");
    Promise.allSettled([
      axios.get(PRESET_URL, { withCredentials: true, timeout: 20000, signal: controller.signal }),
      axios.get(`${PRESET_URL}/pupils`, { withCredentials: true, timeout: 20000, signal: controller.signal }),
    ]).then(([presetResult, pupilResult]) => {
      if (!active) return;
      const data = presetResult.status === "fulfilled" ? presetResult.value.data : null;
      if (data?.preset?.id !== "peterborough" || data.preset.stop_after_return !== true
          || data.preset.duration_minutes !== 40 || data.preset.target_return_minutes !== 35
          || !Array.isArray(data.preset.stops)) {
        throw new Error("Unexpected Part 3 preset response");
      }
      const receipt = data.active_launch;
      if (receipt != null && (!clean(receipt.launch_id)
          || !["to_pupil", "pickup_briefing", "to_centre", "centre_briefing", "circuit", "debrief"].includes(receipt.phase || receipt.stage))) {
        throw new Error("Unexpected active Part 3 route");
      }
      const rows = pupilResult.status === "fulfilled" ? pupilResult.value.data : null;
      if (!receipt && !Array.isArray(rows)) throw new Error("Unexpected pupil list response");
      setPreset(data.preset);
      setActiveLaunch(receipt || null);
      setPupils(part3PupilOptions(rows));
      setLoadState("ready");
    }).catch(() => {
      if (active) setLoadState("error");
    });
    return () => { active = false; controller.abort(); };
  }, [reload]);

  const selectedPupil = useMemo(() => pupils.find((pupil) => pupil.id === studentId), [pupils, studentId]);
  const matchingPupils = pupils.filter((pupil) => normalName(pupil.name).includes(normalName(query)));
  const busy = status !== "ready";
  const routeVerified = preset?.route_readiness === "verified" && preset?.stops?.length > 0;

  const launch = async (event, requestedPupil = selectedPupil, requestId = null) => {
    event?.preventDefault?.();
    if (!requestedPupil || !preset || activeLaunch || loadState !== "ready" || attemptRef.current) return;
    if (!routeVerified) {
      setError(preset.route_message || "The exact Part 3 road circuit needs verification before starting.");
      return;
    }
    const selectedId = requestedPupil.id;
    const attempt = new AbortController();
    attemptRef.current = attempt;
    const current = () => aliveRef.current && attemptRef.current === attempt && !attempt.signal.aborted;
    setError("");
    setStatus("locating");
    try {
      if (!operationRef.current || operationRef.current.studentId !== selectedId) {
        operationRef.current = { studentId: selectedId, id: requestId || createPart3OperationId() };
      }
      const position = await requestPart3LaunchLocation({ signal: attempt.signal });
      if (!current()) return;
      if (position.status !== "granted") {
        setError(locationMessages[position.status] || locationMessages.unavailable);
        setStatus("ready");
        attemptRef.current = null;
        return;
      }
      setStatus("launching");
      const { data } = await axios.post(`${PRESET_URL}/launch`, {
        student_id: selectedId,
        current_location: position.location,
        operation_id: operationRef.current.id,
      }, { withCredentials: true, timeout: 45000, signal: attempt.signal });
      if (!current()) return;
      if (!clean(data?.launch_id) || data?.student?.id !== selectedId
          || !["to_pupil", "pickup_briefing", "to_centre", "centre_briefing", "circuit", "debrief"].includes(data?.phase || data?.stage)) throw new Error("Unexpected Part 3 launch response");
      setStatus("opening");
      navigate(`/work-mode?part3_session=${encodeURIComponent(data.launch_id)}`, {
        state: { part3Launch: data },
      });
    } catch (failure) {
      if (!current()) return;
      setError(launchError(failure));
      setStatus("ready");
      attemptRef.current = null;
    }
  };

  useEffect(() => {
    if (!command || commandHandledRef.current === command.commandId || loadState !== "ready") return;
    commandHandledRef.current = command.commandId;
    const pupil = exactPart3Pupil(pupils, command.pupilName);
    setQuery(command.pupilName);
    setStudentId(pupil?.id || "");
    if (activeLaunch) return;
    if (pupil) launch(null, pupil, command.commandId);
    // One explicitly requested start; rerenders must never replay it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [command, loadState, pupils, activeLaunch]);

  if (loadState === "loading") return <p role="status" className="py-3 text-sm text-slate-600 dark:text-slate-300">Loading your saved route and pupils…</p>;
  if (loadState === "error") return <div role="status" className="space-y-3 text-sm">
    <p>The Part 3 route or pupil list is unavailable. No journey has started.</p>
    <button type="button" data-testid="part3-preset-retry" onClick={() => setReload((value) => value + 1)}
      className="min-h-11 rounded-xl border border-slate-300 px-4 py-2 font-semibold dark:border-slate-600">Retry Part 3</button>
  </div>;

  return <form onSubmit={launch} className="space-y-4" data-testid="part3-preset-form">
    {activeLaunch ? <div className="space-y-3">
      <p className="text-sm" data-testid="part3-preset-active">Your unfinished Part 3 journey with {clean(activeLaunch.student?.name) || "your selected pupil"} is available.</p>
      <button type="button" data-testid="part3-preset-resume" onClick={() => navigate(`/work-mode?part3_session=${encodeURIComponent(activeLaunch.launch_id)}`)}
        className="min-h-12 w-full rounded-xl bg-emerald-700 px-4 py-3 font-bold text-white">Open existing Part 3 journey</button>
    </div> : <>
      {pupils.length ? <div className="space-y-2">
        <label htmlFor={`${fieldId}-search`} className="block text-sm font-bold">Who are you taking?</label>
        <input id={`${fieldId}-search`} data-testid="part3-preset-pupil-search" type="text" value={query}
          disabled={busy} autoComplete="off" maxLength={160} placeholder="Type your pupil’s name"
          onChange={(event) => {
            const value = event.target.value;
            setQuery(value);
            setStudentId(exactPart3Pupil(pupils, value)?.id || "");
            operationRef.current = null;
            setError("");
          }} className="min-h-12 w-full rounded-xl border border-slate-300 bg-white px-3 text-base text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white" />
        <label htmlFor={fieldId} className="block text-sm">Or choose from your pupil list</label>
        <select id={fieldId} data-testid="part3-preset-pupil" value={studentId} disabled={busy}
          onChange={(event) => { setStudentId(event.target.value); operationRef.current = null; setError(""); }}
          className="block min-h-12 w-full min-w-0 rounded-xl border border-slate-300 bg-white px-3 py-2 text-base text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white disabled:opacity-60">
          <option value="">Choose a pupil</option>
          {matchingPupils.map((pupil) => <option key={pupil.id} value={pupil.id}>{pupil.name}</option>)}
        </select>
        {query && !matchingPupils.length && <p role="status" className="text-sm">No matching pupil. Check the name or clear it to see your list.</p>}
        <p className="text-xs text-slate-600 dark:text-slate-300">We’ll use their saved pickup address. No previous pupil is selected automatically.</p>
      </div> : <p data-testid="part3-preset-empty" role="status" className="text-sm">There are no pupils in your list. Add your pupil in Pupils, then reopen Part 3.</p>}
      {!routeVerified && <p data-testid="part3-route-unverified" role="status" className="text-sm text-amber-800 dark:text-amber-200">{preset.route_message || "The exact Part 3 road circuit needs verification before starting."}</p>}
      {error && <p data-testid="part3-preset-error" role="alert" className="break-words text-sm text-rose-700 dark:text-rose-300">{error}</p>}
      <button type="submit" data-testid="part3-preset-launch" disabled={!selectedPupil || busy || !routeVerified}
        className="min-h-14 w-full rounded-xl bg-emerald-700 px-4 py-3 text-base font-bold text-white hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50">
        {status === "locating" ? "Getting your location…" : status === "launching" ? "Preparing pickup route…" : status === "opening" ? "Opening your route…" : "Go to pupil"}
      </button>
      {busy && <p role="status" className="text-sm">Preparing your selected pupil’s Part 3 journey…</p>}
    </>}
    <details className="text-sm text-slate-600 dark:text-slate-300">
      <summary className="min-h-11 cursor-pointer py-3 font-semibold">See the saved roundabouts route</summary>
      <p>Collect your pupil and hold the first briefing there. Travel to Fengate, hold the second briefing, follow this specific roundabouts circuit, then hold the final debrief back at Fengate.</p>
      <p className="mt-2">The fixed 40-minute block starts on arrival at Fengate. Pickup, its briefing and transfer are outside it. Return target: minute 35. The two centre conversations have six minutes combined. Traffic can reduce practice time.</p>
      <ol data-testid="part3-preset-roads" className="mt-2 list-decimal space-y-1 pl-5">
        {preset.stops.map((stop, index) => <li key={stop.id || index}>{clean(stop.label || stop.name)}</li>)}
      </ol>
    </details>
  </form>;
}

function PresetLauncher({ ownerId }) {
  const [open, setOpen] = useState(false);
  const [command, setCommand] = useState(null);
  const panelId = useId();
  useEffect(() => {
    const receive = () => {
      const value = takePart3Command(ownerId);
      if (value) { setCommand(value); setOpen(true); }
    };
    receive();
    window.addEventListener("ndg:part3-start", receive);
    return () => window.removeEventListener("ndg:part3-start", receive);
  }, [ownerId]);
  return <section data-testid="part3-preset" className="overflow-hidden rounded-2xl border border-emerald-200 bg-white text-slate-900 dark:border-emerald-800 dark:bg-slate-900 dark:text-white">
    <button type="button" data-testid="part3-preset-toggle" aria-expanded={open} aria-controls={panelId}
      onClick={() => { setCommand(null); setOpen(true); }}
      className="flex min-h-24 w-full flex-col items-center justify-center gap-1 bg-emerald-700 px-4 py-5 text-center text-white hover:bg-emerald-600 focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-emerald-500">
      <span className="text-2xl font-black leading-tight">Part 3 — Start Now</span>
      <span className="text-sm">Roundabouts · Peterborough · Choose your pupil</span>
    </button>
    {open && <div id={panelId} className="space-y-3 p-4">
      <button type="button" data-testid="part3-preset-close" onClick={() => { setCommand(null); setOpen(false); }}
        className="min-h-11 rounded-lg border border-slate-300 px-4 text-sm font-semibold dark:border-slate-600">Back to calendar</button>
      <PresetForm command={command} />
    </div>}
  </section>;
}

export default function Part3RoutePreset() {
  const { user } = useAuth();
  if (resolveEffectiveRole(user) !== "pdi") return null;
  const scope = [user?.id || user?._id, user?.operating_mode, user?.admin_view_role,
    user?.founder_mode_subject_user_id].join(":");
  return <PresetLauncher key={scope} ownerId={String(user?._id || user?.id || "")} />;
}

